import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Award, ArrowLeft, Loader2, Copy, ExternalLink, Globe, Lock, ShieldCheck,
  ShieldOff, AlertTriangle, History,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const STATUS_META = {
  claimed:  { label: "Claimed",   tone: "green"  },
  verified: { label: "Verified",  tone: "green"  },
  issued:   { label: "Issued",    tone: "yellow" },
  active:   { label: "Active",    tone: "yellow" },
  revoked:  { label: "Revoked",   tone: "red"    },
};

const EVENT_LABEL = {
  "certificate.issue": "Issued",
  "certificate.assign": "Assigned to collector",
  "certificate.claim": "Claim opened",
  "certificate.claim.code": "Claim code redeemed",
  "certificate.claim.legacy.submitted": "Legacy proof submitted",
  "certificate.visibility_changed": "Visibility changed",
  "certificate.album_linked": "Album linked",
  "certificate.album_unlinked": "Album unlinked",
  "certificate.void": "Revoked",
  "certificate.restore": "Restored",
};

export default function CertificateDetail() {
  const { cert_number } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorStatus, setErrorStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/collector-auth/certificates/${cert_number}`);
      setData(r.data);
      setStatus(200);
    } catch (e) {
      setErrorStatus(e?.response?.status || 500);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [cert_number]);

  const toggle = async () => {
    if (!data?.certificate) return;
    setBusy(true);
    try {
      await api.patch(`/collector-auth/certificates/${cert_number}/visibility`, {
        is_public: !data.certificate.is_public,
      });
      toast.success("Visibility updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update.");
    } finally { setBusy(false); }
  };

  if (loading) return (
    <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-16 grid place-items-center" data-testid="cert-detail-loading">
      <Loader2 className="animate-spin" size={28} />
    </div>
  );
  if (errorStatus || !data?.certificate) {
    const notFound = errorStatus === 404;
    return (
      <div className="rounded-2xl border-2 border-[var(--panini-red)] bg-red-50 p-8 text-center" data-testid="cert-detail-error">
        <AlertTriangle className="mx-auto text-[var(--panini-red)]" size={32} />
        <h2 className="font-display text-2xl mt-3">{notFound ? "Certificate not found." : "Something went wrong."}</h2>
        <p className="text-sm text-[var(--smoke)] mt-2">
          {notFound ? "This certificate either doesn't exist or isn't attached to your account." : "Please try again in a moment."}
        </p>
        <Link to="/dashboard/certificates" className="btn-ghost text-sm mt-4 inline-flex" data-testid="cert-detail-back">
          <ArrowLeft size={13} /> Back to My Certificates
        </Link>
      </div>
    );
  }

  const c = data.certificate;
  const meta = STATUS_META[c.status] || { label: c.status, tone: "gray" };
  const verifyUrl = `/certificates/verify/${c.cert_number}`;
  const isRevoked = c.verification_status === "revoked";

  const copy = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}${verifyUrl}`);
    toast.success("Verification link copied.");
  };

  const toneCls = {
    green:  "bg-emerald-100 text-emerald-900 border-emerald-900",
    yellow: "bg-[var(--panini-yellow)] text-[var(--ink)] border-[var(--ink)]",
    red:    "bg-red-100 text-red-900 border-red-900",
  }[meta.tone] || "bg-neutral-100 text-[var(--ink)] border-[var(--ink)]";

  return (
    <div className="space-y-6" data-testid="cert-detail-page">
      <Link to="/dashboard/certificates" className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--smoke)] hover:text-[var(--ink)]" data-testid="cert-detail-back">
        <ArrowLeft size={14} /> Back to My Certificates
      </Link>

      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[6px_6px_0_var(--ink)]" data-testid="cert-detail-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="kicker kicker-red">Certificate</div>
            <h1 className="font-display text-3xl md:text-4xl mt-1 font-mono break-all" data-testid="cert-detail-number">{c.cert_number}</h1>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className={`px-3 py-1 rounded-full border-2 text-xs uppercase tracking-widest font-bold ${toneCls}`} data-testid="cert-detail-status">
                {meta.label}
              </span>
              {isRevoked
                ? <span className="inline-flex items-center gap-1 text-xs text-red-800"><ShieldOff size={12} /> This certificate is revoked.</span>
                : <span className="inline-flex items-center gap-1 text-xs text-emerald-800"><ShieldCheck size={12} /> Verified by CollectorUp.</span>}
            </div>
          </div>
          <button
            type="button"
            onClick={toggle}
            disabled={busy || isRevoked}
            data-testid="cert-detail-visibility"
            aria-pressed={c.is_public}
            className={`text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full border-2 inline-flex items-center gap-1.5 transition disabled:opacity-40 ${
              c.is_public
                ? "bg-emerald-100 border-emerald-900 text-emerald-900"
                : "bg-white border-[var(--ink)] text-[var(--smoke)]"
            }`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : (c.is_public ? <Globe size={12} /> : <Lock size={12} />)}
            {c.is_public ? "Visible on Profile" : "Private"}
          </button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
          <Field label="Album" value={c.album_title || "—"} testid="cert-detail-album" />
          <Field label="Completion date" value={c.completion_date || "—"} testid="cert-detail-completion" />
          <Field label="Issue date" value={c.issued_at ? new Date(c.issued_at).toLocaleDateString() : "—"} testid="cert-detail-issued" />
          <Field label="Claimed" value={c.claimed_at ? new Date(c.claimed_at).toLocaleDateString() : "—"} testid="cert-detail-claimed" />
        </div>

        {c.public_notes && (
          <div className="mt-4 p-4 rounded-lg border-2 border-[var(--panini-yellow)] bg-[var(--panini-yellow-soft)] text-sm" data-testid="cert-detail-public-notes">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--panini-red)]">CollectorUp notes</div>
            <p className="mt-1">{c.public_notes}</p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="btn-red text-sm" data-testid="cert-detail-verify-open">
            <ExternalLink size={14} /> Public verification page
          </a>
          <button type="button" onClick={copy} className="btn-ghost text-sm" data-testid="cert-detail-copy">
            <Copy size={14} /> Copy verification link
          </button>
        </div>
      </div>

      {/* History */}
      <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-6" data-testid="cert-detail-history">
        <div className="flex items-center gap-2">
          <History size={14} /> <div className="kicker kicker-black">Certificate history</div>
        </div>
        {(data.history || []).length === 0 ? (
          <div className="mt-3 text-sm text-[var(--smoke)]">No history yet.</div>
        ) : (
          <ol className="mt-4 space-y-2">
            {data.history.map((e, i) => (
              <li key={i} className="flex items-center justify-between text-sm border-b border-[var(--stone)] last:border-b-0 py-1.5" data-testid={`cert-detail-history-${i}`}>
                <span className="font-bold">{EVENT_LABEL[e.action] || e.action}</span>
                <span className="text-xs text-[var(--smoke)]">{new Date(e.at).toLocaleString()}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</div>
      <div className="mt-1 font-bold">{value}</div>
    </div>
  );
}
